<template>
  <router-link :to="'/auction/' + auction.id" class="no-underline">
    <div class="listing-item">

      <div class="image-container" :style="{ backgroundImage: 'url(' + photoUrl(auction) + ')' }">
        <div class="tenant-logo" :style="{ backgroundImage: 'url(' + tenantLogoUrl(auction.tenantData) + ')' }"></div>
        <div v-if="isSold()" class="sold-overlay"><span>Sold!</span></div>
        <div v-else-if="isOpen()" class="register-now-overlay"><span>Register Now!</span></div>
        <div v-else class="status-box" :class="'status-box-' + auction.status">{{ auctionStatusLabel(auction.status) }}</div>
      </div>

      <div class="property-details">
        <div class="property-name">{{ auction.address | truncate(50) }}</div>
        <div class="light">{{ auction.zip }} {{ auction.city }}, {{ auction.state }}</div>
        <div class="light">
          {{ auction.metadata.buildingSize | prettySF }}
          | {{ propertyTypeLabel(auction.propertyType) }}
          | {{ auction.yearBuilt }}
          | <v-icon style="font-size:1.1em">people</v-icon> {{ numberOfBidders }}
        </div>
        <div class="action-area pointer" @click.prevent="toggleWatchlist">
          <img :src="require('../assets/watchlist-true.png')" v-if="watchlisted" class="watchlist-icon" />
          <img :src="require('../assets/watchlist-false.png')" v-else class="watchlist-icon" />
        </div>
      </div>

      <div class="property-details-bottom">
        <div v-if="auction.status === auctionStatuses.OPEN.value || auction.status === auctionStatuses.FREEZE.value" class="auction-time">
          Starts {{ auction.startDate | prettyDateTime(auction.metadata.timezone) }}
        </div>
        <div v-else-if="auction.status === auctionStatuses.AUCTION.value" class="auction-time">
          Ends {{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}
        </div>
        <div v-else-if="auction.status === auctionStatuses.CANCELLED.value" class="auction-time">
          Was cancelled
        </div>
        <div v-else class="auction-time">
          Ended {{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}
        </div>

        <div v-if="auction.status === auctionStatuses.OPEN.value" class="auction-price open">
          <div>Starting bid: {{ auction.startingPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.FREEZE.value" class="auction-price freeze">
          <div>Starting bid: {{ auction.startingPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.AUCTION.value" class="auction-price auction">
          <div v-if="auction.currentPrice > 0">Current bid: {{ auction.currentPrice | prettyMoney }}</div>
          <div v-else>Starting bid: {{ auction.startingPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.PROCESSING.value" class="auction-price processing">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.IN_ESCROW.value" class="auction-price in-escrow">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.SOLD.value" class="auction-price sold">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.CANCELLED.value" class="auction-price cancelled">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
        <div v-else-if="auction.status === auctionStatuses.CLOSED.value" class="auction-price closed">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
        <div v-else class="auction-price closed">
          <div>Highest bid: {{ auction.currentPrice | prettyMoney }}</div>
        </div>
      </div>

    </div>
  </router-link>
</template>

<script>
  import { propertyTypeLabel, auctionStatuses, auctionStatusLabel, tenantLogoUrl } from '../utils/PfxUtils.js'
  import { mapGetters } from 'vuex'

  export default {
    name: 'ListingItem',
    props: {
      auction: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters([
        'getWatchlist'
      ]),
      currentPrice() {
        return Math.max(this.auction.startingPrice, this.auction.currentPrice)
      },
      numberOfBidders() {
        return this.auction.participants.applicant + this.auction.participants.buyer + this.auction.participants.winner + this.auction.participants.abandon
      },
      auctionStatuses() {
        return auctionStatuses
      },
      watchlisted() {
        return this.getWatchlist.indexOf(this.auction.id) !== -1
      }
    },
    created() {
      if (!this.auction.metadata) { this.auction.metadata = {} }
    },
    methods: {
      propertyTypeLabel,
      auctionStatusLabel,
      tenantLogoUrl,

      photoUrl(auction) {
        if (auction.metadata && auction.metadata.photos && Array.isArray(auction.metadata.photos) && auction.metadata.photos.length > 0) {
          return auction.metadata.photos[0]
        }
        return require('../assets/placeholder-photos.jpg')
      },

      isOpen() {
        return this.auction.status === this.auctionStatuses.OPEN.value
      },

      isSold() {
        return this.auction.status === this.auctionStatuses.SOLD.value &&
          this.auction.currentPrice >= this.auction.reservePrice
      },

      toggleWatchlist() {
        this.$store.dispatch('toggleWatchlistStatus', this.auction.id)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  $box-border-color: #eee;
  $container-height: 19.5rem;
  $image-height: 10.5rem;

  .listing-item {
    position: relative;
    margin-top: 1rem;
    margin-bottom: 1rem;
    height: $container-height;
    background-color: $color-pure-white;
    cursor: pointer;
  }
  .listing-item:hover {
    .register-now-overlay {
      top: $image-height;
      span {
        color: transparent;
      }
    }
  }

  .image-container {
    position: relative;
    width: 100%;
    background-size: cover;
    background-position: center;
    height: $image-height;

    .tenant-logo {
      background-size: cover;
      background-position: center;
      width: 3rem;
      height: 3rem;
      position: absolute;
      left: 0.5rem;
      top: 0.5rem;
      z-index: 1; // should be above sold-overlay
    }

    .status-box {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      display: inline-block;
      font-weight: 600;
      font-size: 0.85rem;
      padding: 0.25rem;
      background-color: rgba(0, 0, 0, 0.8);
      color: $color-white;
      text-align: center;
    }
    .status-box-OPEN { background-color: rgba($color-auction-open, 1); color: #333; }
    .status-box-FREEZE { background-color: rgba($color-pfx-brand, 1); }
    .status-box-AUCTION { background-color: rgba($color-in-auction, 1); color: #333; }

    .register-now-overlay, .sold-overlay {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: inline-block;
      font-weight: 400;
      font-size: 1.4rem;
      text-align: center;
      transition: top .5s ease;
      span {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        transition: color .5s ease;
      }
    }
    .register-now-overlay {
      background-color: rgba(255, 255, 255, 0.75);
      color: #000;
    }
    .sold-overlay {
      background-color: rgba(0, 0, 0, 0.5);
      color: $color-white;
    }
  }

  img.property-image {
    width: 100%;
  }

  .property-details {
    width: 100%;
    padding: .75rem;
    font-size: 0.7rem;
    position: relative;
    border: solid $box-border-color;
    border-width: 0 1px;

    .property-name {
      font-weight: 400;
      font-size: 0.8rem;
      margin-bottom: .25rem;
      color: black;
    }
    div.light {
      color: #808080;
    }

    .action-area {
      position: absolute;
      top: .5rem;
      right: 0;
    }
  }

  .property-details-bottom {
    width: 100%;
    position: absolute;
    bottom: 0;
    font-size: 0.7rem;
    border: solid $box-border-color;
    border-width: 0 1px 1px;

    .auction-time {
      padding: .75rem;
      margin-top: 0.8rem;
      color: #aaa;
      font-weight: 600;
    }

    .auction-price {
      width: 100%;
      font-size: 0.85rem;
      font-weight: 600;
      padding: 3px 0;
      text-align: center;
      background-color: #424242;
      color: $color-white;
    }
    //.auction-price.open { background-color: #424242; color: #fff; }
    //.auction-price.auction { background-color: #424242; color: #fff; }
    //.auction-price.processing { background-color: #424242; color: #fff; }
    //.auction-price.closed { background-color: #424242; color: #fff; }
    //.auction-price.sold { background-color: #424242; color: #fff; }
  }

  .watchlist-icon {
    margin-right: 0.75rem;
  }

</style>



// WEBPACK FOOTER //
// src/components/ListingItem.vue